import { PropsWithChildren, useEffect, useLayoutEffect, useRef } from 'react'
import { Icon, Tooltip } from '@skedulo/breeze-ui-react'
import { UIErrorCode } from '../constants'
import { useErrorAccumulator, ERROR_THRESHOLD } from './error-accumulator'
import { TooManyErrorsFallback } from './too-many-errors-fallback'
import type { ErrorFallbackProps } from './error-fallback'

export type CompactErrorFallbackProps = ErrorFallbackProps

export const CompactErrorFallback = ({
  code = UIErrorCode.UNKNOWN,
  traceId,
  error,
}: PropsWithChildren<CompactErrorFallbackProps>) => {
  const myId = useRef(Symbol()).current
  const {
    count,
    escalatorId,
    increment,
    decrement,
    claimEscalation,
    releaseEscalation,
  } = useErrorAccumulator()

  useEffect(() => {
    increment()
    return () => {
      releaseEscalation(myId)
      decrement()
    }
  }, [increment, decrement, releaseEscalation, myId])

  useLayoutEffect(() => {
    if (count >= ERROR_THRESHOLD && escalatorId === null) {
      claimEscalation(myId)
    }
  }, [count, escalatorId, claimEscalation, myId])

  if (count >= ERROR_THRESHOLD) {
    if (escalatorId === myId) {
      return <TooManyErrorsFallback />
    }
    return null
  }

  const tooltipContent = [
    'Unable to load this component due to a technical problem.',
    error?.message ? `Error: ${error.message}` : null,
    `Error code: ${code}`,
    traceId ? `Trace ID: ${traceId}` : null,
  ]
    .filter(Boolean)
    .join('\n')

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: 'var(--brz-spacing-2)',
        fontSize: 'var(--brz-font-size-sm)',
        color: 'var(--brz-color-red-700)',
      }}
    >
      <Tooltip content={tooltipContent} placement="top">
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--brz-spacing-1)',
            cursor: 'help',
          }}
        >
          <Icon icon="warning" size={16} />
          <span>Component failed to load</span>
        </span>
      </Tooltip>
    </div>
  )
}
